import { useEffect } from 'react';
import ttiPolyfill from 'tti-polyfill';

import usePageContext from '@rover/react-lib/src/context/usePageContext';
import { camelizeString } from '@rover/rsdk/src/modules/Network/object-utilities';
import { effects as baseTemplateEffects } from '@rover/shared/js/ssr/BaseTemplate/BaseTemplate.duck';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import useCurrentUserShim from '../hooks/useCurrentUserShim';
import useDucks from '../hooks/useDucks';

import usePerfTrackingEvents from './usePerfTrackingEvents';


const PERFORMANCE_METRIC_KEYS = [
  'unloadEventStart',
  'unloadEventEnd',
  'redirectStart',
  'redirectEnd',
  'fetchStart',
  'domainLookupStart',
  'domainLookupEnd',
  'connectStart',
  'connectEnd',
  'secureConnectionStart',
  'requestStart',
  'responseStart',
  'responseEnd',
  'domLoading',
  'domInteractive',
  'domContentLoadedEventStart',
  'domContentLoadedEventEnd',
  'domComplete',
  'loadEventStart',
  'loadEventEnd',
];

const getPerformanceMetrics = (): Record<string, number> => {
  const { timing } = window.performance;
  const startTime = timing.navigationStart;
  const metrics = PERFORMANCE_METRIC_KEYS.reduce((accumulator, key) => {
    const eventTime = timing[key];

    if (eventTime > 0) {
      const elapsed = eventTime - startTime;
      return { ...accumulator, [key]: elapsed };
    }

    return accumulator;
  }, {});
  const paintEntries =
    (window.performance &&
      window.performance.getEntriesByType &&
      window.performance.getEntriesByType('paint')) ||
    [];
  paintEntries.forEach((paintPerformanceEntry) => {
    const paintKey = camelizeString(paintPerformanceEntry.name);
    metrics[paintKey] = parseInt(paintPerformanceEntry.startTime as unknown as string, 10);
  });
  return metrics;
};

const firePageViewPerformanceEvent = async (): Promise<void> => {
  const performanceMetrics = window.performance?.timing ? getPerformanceMetrics() : {};
  const timeToInteractive = await ttiPolyfill.getFirstConsistentlyInteractive();
  const performanceEvent = {
    event: 'pageview-performance',
    ...performanceMetrics,
    timeToInteractive,
  };
  return fireDataLayerEvent(performanceEvent);
};

const effects = {
  fireCrossPageEvents: baseTemplateEffects.fireCrossPageEvents,
  fireCWVMetricsEvents: baseTemplateEffects.fireCWVMetricsEvents,
};

const usePerfTrackingEventsShim = (): void => {
  const route = usePageContext();
  const { isAuthenticated = false } = useCurrentUserShim();
  const { isReactQueryRoute } = route;
  const { data: selectedData } = useDucks({ effects });
  const data = usePerfTrackingEvents();

  const events = isReactQueryRoute ? data : selectedData;
  const { fireCrossPageEvents, fireCWVMetricsEvents } = events;

  useEffect(() => {
    firePageViewPerformanceEvent();
    fireCWVMetricsEvents(route, isAuthenticated);
    fireCrossPageEvents();
  }, []);
};

export default usePerfTrackingEventsShim;
